<template>
  <section class="trainerpoint-live">
    <header class="trainerpoint-header">
      <div class="trainerpoint-title">
        <span>TRAINERPOINT LIVE</span>
        <h1>Kunden im Center</h1>
        <p>Direkt einen Termin planen, eine Interaktion erfassen oder die Kundenakte öffnen.</p>
      </div>

      <div class="trainerpoint-header__actions">
        <span v-if="lastUpdatedLabel" class="trainerpoint-updated">{{ lastUpdatedLabel }}</span>
        <button type="button" class="icon-button" :disabled="loading" aria-label="Live-Übersicht aktualisieren" @click="fetchLiveFloor(true, true)">
          <v-icon :class="{ spinning: loading }">mdi-refresh</v-icon>
        </button>
        <button v-if="closable" type="button" class="icon-button" aria-label="Trainerpoint Live schliessen" @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </button>
      </div>
    </header>

    <div class="trainerpoint-toolbar">
      <div class="view-tabs" role="tablist" aria-label="Anzeige filtern">
        <button
          v-for="view in viewOptions"
          :key="view.value"
          type="button"
          role="tab"
          :aria-selected="activeView === view.value"
          :class="{ active: activeView === view.value }"
          @click="setActiveView(view.value)">
          <v-icon>{{ view.icon }}</v-icon>
          {{ view.label }}
          <span>{{ view.count }}</span>
        </button>
      </div>

      <label class="customer-search">
        <v-icon>mdi-magnify</v-icon>
        <input v-model.trim="search" type="search" autocomplete="off" placeholder="Name oder E-Mail suchen" />
        <button v-if="search" type="button" aria-label="Suche löschen" @click="search = ''">
          <v-icon>mdi-close-circle</v-icon>
        </button>
      </label>
    </div>

    <div v-if="errorMessage && !loading" class="state-message state-message--error">
      <v-icon>mdi-alert-circle-outline</v-icon>
      <div>
        <strong>Live-Übersicht konnte nicht geladen werden</strong>
        <span>{{ errorMessage }}</span>
      </div>
      <button type="button" @click="fetchLiveFloor(true)">Nochmal versuchen</button>
    </div>

    <div v-if="loading && !entries.length" class="skeleton-grid" aria-label="Live-Übersicht wird geladen">
      <div v-for="index in 8" :key="index" class="customer-skeleton"></div>
    </div>

    <div v-else-if="!visibleEntries.length" class="empty-state">
      <v-icon>{{ search ? "mdi-account-search-outline" : emptyState.icon }}</v-icon>
      <strong>{{ search ? "Keine passende Person gefunden" : emptyState.title }}</strong>
      <span>{{ search ? "Prüfe den Namen oder wechsle zu «Alle»." : emptyState.text }}</span>
      <button v-if="search" type="button" @click="search = ''">Suche zurücksetzen</button>
    </div>

    <div v-else class="customer-grid">
      <article v-for="entry in visibleEntries" :key="entry._id" class="customer-card" :class="{ 'customer-card--attention': entry.needsAttention }">
        <div class="customer-card__head">
          <div class="customer-avatar" :class="{ 'customer-avatar--initials': !getPicture(entry) }">
            <img
              v-if="getPicture(entry)"
              :src="getPicture(entry)"
              :alt="displayName(entry)"
              width="54"
              height="54"
              loading="lazy"
              decoding="async"
              @error="markImageFailed(entry)" />
            <span v-else>{{ getInitials(entry) }}</span>
            <i aria-hidden="true"></i>
          </div>

          <div class="customer-identity">
            <h2>{{ displayName(entry) }}</h2>
            <div class="visit-meta">
              <span><v-icon>mdi-login</v-icon>{{ formatTime(entry.checkinDate) }}</span>
              <span><v-icon>mdi-clock-outline</v-icon>{{ formatVisitDuration(entry) }}</span>
            </div>
          </div>
        </div>

        <div v-if="visibleSignals(entry).length" class="signal-list">
          <span v-for="signal in visibleSignals(entry)" :key="signal.key" class="signal-chip" :class="`signal-chip--${signal.tone}`">
            <v-icon>{{ signal.icon }}</v-icon>
            {{ signal.label }}
          </span>
        </div>

        <div v-if="entry.careUi && entry.careUi.ready" class="care-overview">
          <button
            type="button"
            class="care-status"
            :class="`care-status--${entry.careUi.trainingPlan.state}`"
            title="Trainingspläne in der Kundenakte öffnen"
            @click="openCareSection(entry, 'training')">
            <span class="care-status__icon"
              ><v-icon>{{ entry.careUi.trainingPlan.icon }}</v-icon></span
            >
            <span class="care-status__copy">
              <small>TRAININGSPLAN</small>
              <strong>{{ entry.careUi.trainingPlan.label }}</strong>
              <span>{{ entry.careUi.trainingPlan.meta }}</span>
            </span>
            <v-icon class="care-status__arrow">mdi-chevron-right</v-icon>
          </button>

          <button
            type="button"
            class="care-status"
            :class="`care-status--${entry.careUi.anamnese.state}`"
            title="Anamnese in der Kundenakte öffnen"
            @click="openCareSection(entry, 'anamnese')">
            <span class="care-status__icon"
              ><v-icon>{{ entry.careUi.anamnese.icon }}</v-icon></span
            >
            <span class="care-status__copy">
              <small>ANAMNESE</small>
              <strong>{{ entry.careUi.anamnese.label }}</strong>
              <span>{{ entry.careUi.anamnese.meta }}</span>
            </span>
            <v-icon class="care-status__arrow">mdi-chevron-right</v-icon>
          </button>
        </div>

        <button
          v-if="entry.recommendation"
          type="button"
          class="trainer-recommendation"
          :class="`trainer-recommendation--${entry.recommendation.tone}`"
          @click="executeRecommendation(entry)">
          <span class="trainer-recommendation__icon"
            ><v-icon>{{ entry.recommendation.icon }}</v-icon></span
          >
          <span class="trainer-recommendation__copy">
            <small>EMPFEHLUNG FÜR DICH</small>
            <strong>{{ entry.recommendation.title }}</strong>
            <span>{{ entry.recommendation.text }}</span>
          </span>
          <span class="trainer-recommendation__action">
            {{ entry.recommendation.actionLabel }}
            <v-icon>mdi-arrow-right</v-icon>
          </span>
        </button>

        <button
          v-if="entry.todayAppointment || entry.nextAppointment"
          type="button"
          class="appointment-preview"
          :disabled="!appointmentFor(entry)._id"
          @click="openExistingAppointment(entry, appointmentFor(entry))">
          <span class="appointment-preview__icon"><v-icon>mdi-calendar-check-outline</v-icon></span>
          <span class="appointment-preview__content">
            <small>{{ entry.todayAppointment ? "Heute" : "Nächster Termin" }}</small>
            <strong>{{ appointmentLabel(appointmentFor(entry)) }}</strong>
            <span v-if="appointmentFor(entry).employee">bei {{ appointmentFor(entry).employee.name }}</span>
          </span>
          <v-icon class="appointment-preview__arrow">mdi-chevron-right</v-icon>
        </button>

        <div v-else class="no-appointment-hint">
          <v-icon>mdi-calendar-blank-outline</v-icon>
          Noch kein kommender Termin geplant
        </div>

        <div class="customer-actions">
          <button
            type="button"
            class="action-button action-button--primary"
            :disabled="!hasLinkedUser(entry)"
            @click="handleAppointmentAction(entry)">
            <v-icon>mdi-calendar-plus</v-icon>
            Termin planen
          </button>
          <button
            type="button"
            class="action-button action-button--secondary"
            :disabled="!hasLinkedUser(entry)"
            @click="$emit('create-interaction', entry)">
            <v-icon>mdi-message-text-plus-outline</v-icon>
            Interaktion
          </button>
          <button
            type="button"
            class="action-button action-button--file"
            :disabled="!hasLinkedUser(entry)"
            aria-label="Kundenakte öffnen"
            title="Kundenakte öffnen"
            @click="selectEntry(entry)">
            <v-icon>mdi-account-arrow-right-outline</v-icon>
          </button>
        </div>

        <div v-if="!hasLinkedUser(entry)" class="no-account-note">
          <v-icon>mdi-account-alert-outline</v-icon>
          Dieser Check-in ist mit keinem roody-Konto verknüpft.
        </div>
      </article>
    </div>

    <div v-if="hasMoreEntries" class="load-more">
      <button type="button" @click="renderLimit += pageSize">
        Weitere {{ Math.min(pageSize, filteredEntries.length - visibleEntries.length) }} Personen anzeigen
      </button>
      <span>{{ visibleEntries.length }} von {{ filteredEntries.length }}</span>
    </div>

    <div v-if="truncated" class="limit-note">
      <v-icon>mdi-information-outline</v-icon>
      Aus Performancegründen werden maximal 200 gleichzeitig anwesende Personen angezeigt.
    </div>

    <transition name="template-fade">
      <div v-if="templateChooserOpen" class="template-overlay" role="dialog" aria-modal="true" @click.self="closeTemplateChooser">
        <section class="template-sheet">
          <header class="template-sheet__header">
            <div>
              <span>TERMIN PLANEN</span>
              <h2>Was möchtest du für {{ selectedEntryName }} eintragen?</h2>
              <p>Kunde, Datum, Uhrzeit und Mitarbeiter werden im nächsten Schritt bereits vorausgefüllt.</p>
            </div>
            <button type="button" class="icon-button" aria-label="Vorlagenauswahl schliessen" @click="closeTemplateChooser">
              <v-icon>mdi-close</v-icon>
            </button>
          </header>

          <label class="template-search">
            <v-icon>mdi-magnify</v-icon>
            <input v-model.trim="templateSearch" type="search" autocomplete="off" placeholder="Terminart suchen" />
            <button v-if="templateSearch" type="button" aria-label="Suche löschen" @click="templateSearch = ''">
              <v-icon>mdi-close-circle</v-icon>
            </button>
          </label>

          <div v-if="filteredAppointmentTemplates.length" class="template-grid">
            <button
              v-for="template in filteredAppointmentTemplates"
              :key="templateKey(template)"
              type="button"
              class="template-option"
              @click="chooseAppointmentTemplate(template)">
              <span class="template-option__icon" :style="{ background: template.color || '#079653' }">
                <v-icon color="white">{{ template.icon || "mdi-calendar" }}</v-icon>
              </span>
              <span class="template-option__text">
                <strong>{{ template.title || "Termin" }}</strong>
                <small>{{ Number(template.duration) || 60 }} Minuten</small>
              </span>
              <v-icon class="template-option__arrow">mdi-chevron-right</v-icon>
            </button>
          </div>

          <div v-else class="template-empty">
            <v-icon>mdi-calendar-search-outline</v-icon>
            <strong>Keine passende Terminart gefunden</strong>
            <span>Prüfe den Suchbegriff.</span>
          </div>
        </section>
      </div>
    </transition>
  </section>
</template>

<script>
import axios from "axios"

// Diese Hinweise werden bereits in der grossen Betreuungsanzeige dargestellt
// und deshalb nicht zusätzlich als kleine Chips wiederholt.
const SUPPRESSED_CHIP_SIGNAL_KEYS = new Set(["new_member", "anamnese_missing", "anamnese_old", "training_plan_missing", "training_plan_old"])

export default {
  name: "WorkCalendarCheckinPool",

  props: {
    closable: {
      type: Boolean,
      default: false,
    },
    appointmentTemplates: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      entries: [],
      activeView: "all",
      search: "",
      loading: false,
      errorMessage: "",
      generatedAt: null,
      refreshAfterMs: 60000,
      refreshTimer: null,
      clockTimer: null,
      renderLimit: 24,
      pageSize: 24,
      failedImages: {},
      truncated: false,
      requestSequence: 0,
      destroyed: false,
      templateChooserOpen: false,
      selectedActionEntry: null,
      templateSearch: "",
    }
  },

  computed: {
    liveSummary() {
      return this.entries.reduce(
        (summary, entry) => {
          summary.present += 1
          if (entry.needsAttention) summary.attention += 1
          if (this.hasSignal(entry, "appointment_soon")) summary.appointmentsSoon += 1
          if (this.hasSignal(entry, "no_upcoming")) summary.noUpcoming += 1
          return summary
        },
        { present: 0, attention: 0, appointmentsSoon: 0, noUpcoming: 0 },
      )
    },

    viewOptions() {
      return [
        { value: "all", label: "Alle", icon: "mdi-account-multiple-outline", count: this.liveSummary.present },
        { value: "attention", label: "Empfehlungen", icon: "mdi-lightbulb-on-outline", count: this.liveSummary.attention },
        { value: "appointments", label: "Termin bald", icon: "mdi-calendar-clock-outline", count: this.liveSummary.appointmentsSoon },
        { value: "no_upcoming", label: "Ohne Folgetermin", icon: "mdi-calendar-question-outline", count: this.liveSummary.noUpcoming },
      ]
    },

    filteredEntries() {
      const query = this.normalizeText(this.search)
      const entries = this.entries.filter((entry) => {
        if (this.activeView === "attention" && !entry.needsAttention) return false
        if (this.activeView === "appointments" && !this.hasSignal(entry, "appointment_soon")) return false
        if (this.activeView === "no_upcoming" && !this.hasSignal(entry, "no_upcoming")) return false
        if (!query) return true
        return this.normalizeText(`${this.displayName(entry)} ${entry.email || ""}`).includes(query)
      })

      return entries.sort((left, right) => {
        if (this.activeView === "attention" && Number(right.priority) !== Number(left.priority)) {
          return Number(right.priority) - Number(left.priority)
        }
        return new Date(right.checkinDate || 0) - new Date(left.checkinDate || 0)
      })
    },

    visibleEntries() {
      return this.filteredEntries.slice(0, this.renderLimit)
    },

    hasMoreEntries() {
      return this.visibleEntries.length < this.filteredEntries.length
    },

    lastUpdatedLabel() {
      if (!this.generatedAt) return ""
      const date = new Date(this.generatedAt)
      if (Number.isNaN(date.getTime())) return ""
      return `Stand ${date.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })}`
    },

    emptyState() {
      const states = {
        all: {
          icon: "mdi-account-off-outline",
          title: "Aktuell ist niemand eingecheckt",
          text: "Neue Check-ins erscheinen bei der nächsten Aktualisierung automatisch.",
        },
        attention: {
          icon: "mdi-check-circle-outline",
          title: "Alles im grünen Bereich",
          text: "Für die aktuell Anwesenden gibt es momentan keine konkrete Empfehlung.",
        },
        appointments: {
          icon: "mdi-calendar-clock-outline",
          title: "Kein Termin steht unmittelbar bevor",
          text: "Hier erscheinen Termine, die laufen oder innerhalb der nächsten zwei Stunden beginnen.",
        },
        no_upcoming: {
          icon: "mdi-calendar-check-outline",
          title: "Alle haben einen Folgetermin",
          text: "Aktuell ist niemand ohne kommenden Termin.",
        },
      }
      return states[this.activeView] || states.all
    },

    customerAppointmentTemplates() {
      const seen = new Set()
      return (this.appointmentTemplates || []).filter((template) => {
        if (!template || template.type === "task" || template.type === "pause" || template.requiresCustomer !== true || template.tacServiceType) {
          return false
        }
        const key = this.templateKey(template)
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    },

    filteredAppointmentTemplates() {
      const query = this.normalizeText(this.templateSearch)
      if (!query) return this.customerAppointmentTemplates
      return this.customerAppointmentTemplates.filter((template) =>
        this.normalizeText(`${template.title || ""} ${template.specialType || ""}`).includes(query),
      )
    },

    selectedEntryName() {
      return this.selectedActionEntry ? this.displayName(this.selectedActionEntry) : "diesen Kunden"
    },
  },

  watch: {
    search() {
      this.renderLimit = this.pageSize
    },
  },

  created() {
    this.fetchLiveFloor(true)
  },

  mounted() {
    document.addEventListener("visibilitychange", this.handleVisibilityChange, { passive: true })
    this.startRefreshTimer()
    this.clockTimer = window.setInterval(this.refreshDurations, 60000)
  },

  beforeDestroy() {
    this.destroyed = true
    document.removeEventListener("visibilitychange", this.handleVisibilityChange)
    window.clearInterval(this.refreshTimer)
    window.clearInterval(this.clockTimer)
  },

  methods: {
    async fetchLiveFloor(force = false, refreshCare = false) {
      if (this.loading && !force) return
      const sequence = ++this.requestSequence
      this.loading = true
      this.errorMessage = ""

      try {
        const response = await axios.get("https://app.wellcomefit.ch/api/checkin/pool/live-floor", {
          headers: { Authorization: localStorage.getItem("token") },
          params: refreshCare ? { refreshCare: true } : undefined,
        })
        if (this.destroyed || sequence !== this.requestSequence) return

        this.entries = Array.isArray(response.data?.entries) ? response.data.entries.map((entry) => this.decorateEntry(entry)) : []
        this.generatedAt = response.data?.generatedAt || new Date().toISOString()
        this.refreshAfterMs = Math.max(45000, Number(response.data?.refreshAfterMs) || 60000)
        this.truncated = Boolean(response.data?.truncated)
        this.renderLimit = Math.max(this.pageSize, Math.min(this.renderLimit, this.entries.length || this.pageSize))
        this.startRefreshTimer()
      } catch (error) {
        if (this.destroyed || sequence !== this.requestSequence) return
        this.errorMessage = error?.response?.data?.message || "Bitte prüfe die Verbindung und versuche es erneut."
        console.error("Fehler beim Laden des Trainerpoint Live-Pools:", error)
      } finally {
        if (!this.destroyed && sequence === this.requestSequence) this.loading = false
      }
    },

    fetchCheckinPool() {
      return this.fetchLiveFloor(true)
    },

    startRefreshTimer() {
      window.clearInterval(this.refreshTimer)
      if (typeof document !== "undefined" && document.hidden) return
      this.refreshTimer = window.setInterval(() => {
        if (!document.hidden) this.fetchLiveFloor(false)
      }, this.refreshAfterMs)
    },

    handleVisibilityChange() {
      if (document.hidden) {
        window.clearInterval(this.refreshTimer)
        return
      }
      this.fetchLiveFloor(false)
      this.startRefreshTimer()
    },

    refreshDurations() {
      if (!this.entries.length) return
      const now = Date.now()
      this.entries = this.entries.map((entry) => {
        const started = new Date(entry.checkinDate)
        if (Number.isNaN(started.getTime())) return entry
        return { ...entry, minutesInStudio: Math.max(0, Math.floor((now - started.getTime()) / 60000)) }
      })
    },

    setActiveView(value) {
      this.activeView = value
      this.renderLimit = this.pageSize
    },

    hasSignal(entry, key) {
      return Array.isArray(entry?.signals) && entry.signals.some((signal) => signal.key === key)
    },

    visibleSignals(entry) {
      return (Array.isArray(entry?.signals) ? entry.signals : []).filter((signal) => !SUPPRESSED_CHIP_SIGNAL_KEYS.has(signal.key)).slice(0, 3)
    },

    hasLinkedUser(entry) {
      return Boolean(entry?.user?._id)
    },

    appointmentFor(entry) {
      return entry?.todayAppointment || entry?.nextAppointment || null
    },

    handleAppointmentAction(entry) {
      if (!this.hasLinkedUser(entry)) return
      this.openTemplateChooser(entry)
    },

    openExistingAppointment(entry, appointment) {
      if (!appointment?._id) return
      this.$emit("open-appointment", { entry, appointment })
    },

    openCareSection(entry, section) {
      if (section === "training") {
        this.selectEntry(entry, { section: "training" })
        return
      }
      this.selectEntry(entry, { action: "anamnese" })
    },

    executeRecommendation(entry) {
      const recommendation = entry?.recommendation
      if (!recommendation) return

      if (recommendation.action === "appointment") {
        this.openExistingAppointment(entry, this.appointmentFor(entry))
        return
      }
      if (recommendation.action === "new-appointment") {
        this.handleAppointmentAction(entry)
        return
      }
      if (recommendation.action === "training") {
        this.openCareSection(entry, "training")
        return
      }
      if (recommendation.action === "anamnese") {
        this.openCareSection(entry, "anamnese")
        return
      }
      if (recommendation.action === "interaction") {
        this.$emit("create-interaction", entry)
      }
    },

    openTemplateChooser(entry) {
      if (!this.hasLinkedUser(entry)) return
      if (!this.customerAppointmentTemplates.length) {
        this.$toast?.warning("Es wurde keine Kundentermin-Vorlage gefunden.")
        return
      }
      this.selectedActionEntry = entry
      this.templateSearch = ""
      this.templateChooserOpen = true
    },

    closeTemplateChooser() {
      this.templateChooserOpen = false
      this.templateSearch = ""
      this.selectedActionEntry = null
    },

    chooseAppointmentTemplate(template) {
      if (!this.selectedActionEntry || !template) return
      const entry = this.selectedActionEntry
      this.closeTemplateChooser()
      this.$emit("create-appointment", { entry, template })
    },

    selectEntry(entry, options = {}) {
      if (!this.hasLinkedUser(entry)) {
        this.$toast?.warning("Dieser Check-in ist mit keinem roody-Konto verknüpft.")
        return
      }
      this.$emit("entry-selected", entry, options)
    },

    decorateEntry(entry) {
      const careUi = this.buildCareUi(entry)
      return {
        ...entry,
        careUi,
        recommendation: this.buildRecommendation(entry, careUi),
      }
    },

    buildCareUi(entry) {
      const care = entry?.care
      if (!care) return { ready: false }

      const trainingPlan = care.trainingPlan || {}
      const anamnese = care.anamnese || {}
      const planAge =
        trainingPlan.ageDays !== null && trainingPlan.ageDays !== undefined && Number.isFinite(Number(trainingPlan.ageDays))
          ? Number(trainingPlan.ageDays)
          : null
      const anamneseAge =
        anamnese.ageDays !== null && anamnese.ageDays !== undefined && Number.isFinite(Number(anamnese.ageDays)) ? Number(anamnese.ageDays) : null
      const planState =
        trainingPlan.available === null || trainingPlan.available === undefined
          ? "unavailable"
          : trainingPlan.available === false
          ? "missing"
          : planAge !== null && planAge >= 180
          ? "stale"
          : "current"
      const anamneseState =
        anamnese.available === null || anamnese.available === undefined
          ? "unavailable"
          : anamnese.available === false
          ? "missing"
          : anamneseAge !== null && anamneseAge >= 365
          ? "stale"
          : "current"

      return {
        ready: true,
        trainingPlan: {
          state: planState,
          icon:
            planState === "unavailable"
              ? "mdi-database-alert-outline"
              : planState === "missing"
              ? "mdi-dumbbell"
              : planState === "stale"
              ? "mdi-calendar-refresh-outline"
              : "mdi-arm-flex-outline",
          label:
            planState === "unavailable"
              ? "Status nicht geladen"
              : trainingPlan.available === false
              ? "Nicht vorhanden"
              : trainingPlan.title || (trainingPlan.type === "split" ? "Split-Plan verfügbar" : "Trainingsplan verfügbar"),
          meta:
            planState === "unavailable"
              ? "Über Aktualisieren erneut prüfen"
              : trainingPlan.available === false
              ? "Direkt einen Plan erstellen"
              : this.careTimestampMeta("Aktualisiert", trainingPlan.updatedAt, planAge),
        },
        anamnese: {
          state: anamneseState,
          icon:
            anamneseState === "unavailable"
              ? "mdi-database-alert-outline"
              : anamneseState === "missing"
              ? "mdi-clipboard-pulse-outline"
              : anamneseState === "stale"
              ? "mdi-clipboard-edit-outline"
              : "mdi-clipboard-check-outline",
          label: anamneseState === "unavailable" ? "Status nicht geladen" : anamnese.available === false ? "Nicht vorhanden" : "Anamnese verfügbar",
          meta:
            anamneseState === "unavailable"
              ? "Über Aktualisieren erneut prüfen"
              : anamnese.available === false
              ? "Beim Gespräch gemeinsam erfassen"
              : this.careTimestampMeta("Erfasst", anamnese.updatedAt, anamneseAge),
        },
      }
    },

    buildRecommendation(entry, careUi) {
      const appointment = this.appointmentFor(entry)
      if (this.hasSignal(entry, "appointment_soon") && appointment?._id) {
        return {
          tone: "blue",
          icon: "mdi-calendar-clock",
          title: "Termin jetzt vorbereiten",
          text: `${appointment.start || ""}–${appointment.end || ""} · ${appointment.title || "Kundentermin"}`,
          action: "appointment",
          actionLabel: "Öffnen",
        }
      }

      if (careUi?.ready && careUi.anamnese.state === "missing") {
        return {
          tone: "red",
          icon: "mdi-clipboard-pulse-outline",
          title: "Anamnese gemeinsam erfassen",
          text: "Nutze den Kontakt im Center und halte die gesundheitliche Ausgangslage fest.",
          action: "anamnese",
          actionLabel: "Anamnese",
        }
      }

      if (careUi?.ready && careUi.trainingPlan.state === "missing") {
        return {
          tone: "amber",
          icon: "mdi-dumbbell",
          title: "Trainingsplan aufbauen",
          text: "Für diese Person ist noch kein Trainingsplan hinterlegt.",
          action: "training",
          actionLabel: "Plan erstellen",
        }
      }

      if (careUi?.ready && careUi.trainingPlan.state === "stale") {
        return {
          tone: "amber",
          icon: "mdi-calendar-refresh-outline",
          title: "Trainingsplan aktualisieren",
          text: "Ziele, Belastung und Übungen prüfen – der letzte Stand ist älter als sechs Monate.",
          action: "training",
          actionLabel: "Plan prüfen",
        }
      }

      if (careUi?.ready && careUi.anamnese.state === "stale") {
        return {
          tone: "violet",
          icon: "mdi-clipboard-edit-outline",
          title: "Anamnese kurz überprüfen",
          text: "Der letzte Stand ist älter als ein Jahr. Frage nach gesundheitlichen Veränderungen.",
          action: "anamnese",
          actionLabel: "Prüfen",
        }
      }

      if (this.hasSignal(entry, "no_upcoming")) {
        return {
          tone: "amber",
          icon: "mdi-calendar-plus-outline",
          title: "Folgetermin vereinbaren",
          text: "In den nächsten 45 Tagen ist noch kein Kundentermin geplant.",
          action: "new-appointment",
          actionLabel: "Termin planen",
        }
      }

      return null
    },

    careTimestampMeta(prefix, value, ageDays) {
      const date = new Date(value)
      const dateLabel = Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "2-digit" })
      const ageLabel = this.formatCareAge(ageDays)
      return [prefix, ageLabel, dateLabel ? `· ${dateLabel}` : ""].filter(Boolean).join(" ")
    },

    formatCareAge(value) {
      const days = Number(value)
      if (!Number.isFinite(days)) return ""
      if (days <= 0) return "heute"
      if (days === 1) return "gestern"
      if (days < 14) return `vor ${days} Tagen`
      if (days < 60) return `vor ${Math.round(days / 7)} Wochen`
      if (days < 365) return `vor ${Math.round(days / 30)} Monaten`
      const years = Math.max(1, Math.round(days / 365))
      return years === 1 ? "vor 1 Jahr" : `vor ${years} Jahren`
    },

    templateKey(template) {
      return String(template?._id || template?.id || `${template?.title || "termin"}-${template?.specialType || ""}`)
    },

    normalizeText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase("de-CH")
    },

    displayName(entry) {
      const firstName = entry?.firstName || entry?.user?.vorname || ""
      const lastName = entry?.lastName || entry?.user?.nachname || ""
      return `${firstName} ${lastName}`.trim() || "Unbekannter Check-in"
    },

    getInitials(entry) {
      const name = this.displayName(entry).split(/\s+/).filter(Boolean)
      return `${name[0]?.[0] || "?"}${name.length > 1 ? name[name.length - 1][0] : ""}`.toUpperCase()
    },

    getPicture(entry) {
      const userId = entry?.user?._id
      const failureState = this.failedImages[entry?._id]
      if (!userId || failureState === "tac") return ""

      if (entry.user.bild && failureState !== "profile") {
        const webp = String(entry.user.bild)
          .replace(/\.[^/.]+$/, ".webp")
          .replace(/^\/+/, "")
        return `/img/profilbilder/${webp}`
      }

      return `https://app.wellcomefit.ch/api/checkin/pool/images/${encodeURIComponent(userId)}`
    },

    markImageFailed(entry) {
      const failureState = this.failedImages[entry?._id]
      if (entry?.user?.bild && failureState !== "profile") {
        this.$set(this.failedImages, entry._id, "profile")
        return
      }
      this.$set(this.failedImages, entry._id, "tac")
    },

    formatTime(value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return "–"
      return date.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
    },

    formatVisitDuration(entry) {
      const total = Math.max(0, Number(entry.minutesInStudio) || 0)
      if (total < 60) return `${total} Min. im Center`
      const hours = Math.floor(total / 60)
      const minutes = total % 60
      return `${hours} Std.${minutes ? ` ${minutes} Min.` : ""} im Center`
    },

    localDateKey(value = new Date()) {
      const date = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(date.getTime())) return ""
      const pad = (number) => String(number).padStart(2, "0")
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    },

    appointmentLabel(appointment) {
      if (!appointment) return ""
      const date = new Date(`${appointment.date}T12:00:00`)
      const dateLabel = appointment.date === this.localDateKey() ? "" : `${date.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit" })} · `
      return `${dateLabel}${appointment.start || ""}–${appointment.end || ""} · ${appointment.title || "Termin"}`
    },
  },
}
</script>

<style scoped>
.trainerpoint-live {
  --tp-green: #079653;
  --tp-green-dark: #067541;
  --tp-ink: #17251f;
  --tp-muted: #65786e;
  --tp-line: #dce8e2;
  min-height: 100%;
  padding: 22px clamp(14px, 2.4vw, 34px) 36px;
  color: var(--tp-ink);
  background: radial-gradient(circle at 94% 0%, rgba(12, 169, 95, 0.1), transparent 25rem), linear-gradient(180deg, #f9fcfa 0%, #f1f6f3 100%);
  font-size: 14px;
}

.trainerpoint-live *,
.trainerpoint-live *::before,
.trainerpoint-live *::after {
  box-sizing: border-box;
}

.trainerpoint-live button,
.trainerpoint-live input {
  font-family: inherit;
}

.trainerpoint-header,
.trainerpoint-header__actions,
.trainerpoint-toolbar,
.customer-card__head,
.visit-meta,
.customer-actions {
  display: flex;
  align-items: center;
}

.trainerpoint-header {
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}

.trainerpoint-title > span,
.template-sheet__header span {
  display: block;
  color: var(--tp-green);
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.14em;
}

.trainerpoint-title h1 {
  margin: 2px 0 3px;
  font-size: clamp(27px, 3vw, 40px);
  line-height: 1.05;
}

.trainerpoint-title p,
.template-sheet__header p {
  margin: 0;
  color: var(--tp-muted);
  font-size: 14px;
}

.trainerpoint-header__actions {
  gap: 9px;
}

.trainerpoint-updated {
  color: var(--tp-muted);
  font-size: 12px;
  white-space: nowrap;
}

.icon-button {
  display: inline-grid;
  width: 48px;
  height: 48px;
  place-items: center;
  border: 1px solid var(--tp-line);
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
}

.icon-button:disabled {
  cursor: default;
  opacity: 0.5;
}

.icon-button >>> .v-icon {
  color: #50655a;
}

.spinning {
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.trainerpoint-toolbar {
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 18px;
  padding: 10px;
  border: 1px solid var(--tp-line);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.86);
}

.view-tabs {
  display: flex;
  align-items: center;
  gap: 5px;
}

.view-tabs > button {
  display: inline-flex;
  min-height: 46px;
  align-items: center;
  gap: 7px;
  padding: 0 13px;
  color: var(--tp-muted);
  font-size: 13px;
  font-weight: 800;
  border: 0;
  border-radius: 13px;
  background: transparent;
  cursor: pointer;
}

.view-tabs > button.active {
  color: #fff;
  background: var(--tp-green);
  box-shadow: 0 8px 18px rgba(7, 150, 83, 0.2);
}

.view-tabs > button >>> .v-icon {
  color: currentColor;
  font-size: 19px;
}

.view-tabs > button span {
  min-width: 23px;
  padding: 2px 6px;
  text-align: center;
  border-radius: 999px;
  background: rgba(101, 120, 110, 0.12);
}

.view-tabs > button.active span {
  background: rgba(255, 255, 255, 0.2);
}

.customer-search,
.template-search {
  display: flex;
  min-height: 48px;
  align-items: center;
  gap: 9px;
  padding: 0 12px;
  border: 1px solid var(--tp-line);
  border-radius: 14px;
  background: #fff;
}

.customer-search {
  width: min(340px, 35vw);
}

.customer-search >>> .v-icon,
.template-search >>> .v-icon {
  color: #7d8d84;
  font-size: 21px;
}

.customer-search input,
.template-search input {
  width: 100%;
  min-width: 0;
  color: var(--tp-ink);
  font-size: 14px;
  border: 0;
  outline: 0;
  background: transparent;
}

.customer-search button,
.template-search button {
  display: grid;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  place-items: center;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.customer-grid,
.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.customer-card {
  position: relative;
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
  padding: 13px;
  overflow: hidden;
  border: 1px solid var(--tp-line);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 10px 25px rgba(38, 70, 53, 0.06);
}

.customer-card::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  background: #cad8d1;
  content: "";
}

.customer-card--attention::before {
  background: #efa521;
}

.customer-card__head {
  gap: 10px;
  min-width: 0;
}

.customer-avatar {
  position: relative;
  display: grid;
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  place-items: center;
  overflow: visible;
  border-radius: 14px;
  background: #dceae3;
}

.customer-avatar img {
  width: 46px;
  height: 46px;
  object-fit: cover;
  border-radius: 14px;
}

.customer-avatar > span {
  color: #17623f;
  font-size: 17px;
  font-weight: 900;
}

.customer-avatar i {
  position: absolute;
  right: -3px;
  bottom: -3px;
  width: 14px;
  height: 14px;
  border: 3px solid #fff;
  border-radius: 50%;
  background: #12ad64;
}

.customer-identity {
  min-width: 0;
  flex: 1;
}

.customer-identity h2 {
  margin: 0 0 3px;
  overflow: hidden;
  font-size: 18px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.visit-meta {
  flex-wrap: wrap;
  gap: 6px 12px;
  color: var(--tp-muted);
}

.visit-meta span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.visit-meta >>> .v-icon {
  color: #7a8e83;
  font-size: 15px;
}

.signal-list {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.signal-chip {
  display: inline-flex;
  min-height: 28px;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  color: #53675d;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid #dce7e1;
  border-radius: 10px;
  background: #f4f8f6;
}

.signal-chip >>> .v-icon {
  color: currentColor;
  font-size: 16px;
}

.signal-chip--blue {
  color: #176fae;
  border-color: #cce7f8;
  background: #edf8fe;
}

.signal-chip--green {
  color: #087b46;
  border-color: #c8ead9;
  background: #edf9f3;
}

.signal-chip--amber {
  color: #a46800;
  border-color: #f4dbad;
  background: #fff7e7;
}

.signal-chip--violet {
  color: #7043a0;
  border-color: #e0ceed;
  background: #f8f0fc;
}

.signal-chip--pink {
  color: #ad316b;
  border-color: #f0c9dc;
  background: #fff1f8;
}

.signal-chip--red {
  color: #b53a3f;
  border-color: #f0c6c8;
  background: #fff1f2;
}

.care-overview {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;
}

.care-status {
  display: flex;
  min-width: 0;
  min-height: 58px;
  align-items: center;
  gap: 7px;
  padding: 6px 8px;
  text-align: left;
  border: 1px solid #cee6d9;
  border-radius: 12px;
  background: #f3faf6;
  cursor: pointer;
}

.care-status__icon {
  display: grid;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  place-items: center;
  color: #087b46;
  border-radius: 10px;
  background: #dcefe5;
}

.care-status__icon >>> .v-icon {
  color: currentColor;
  font-size: 18px;
}

.care-status__copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.care-status__copy small {
  color: #5f7469;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: 0.05em;
}

.care-status__copy strong,
.care-status__copy span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.care-status__copy strong {
  color: #173e2b;
  font-size: 13px;
  line-height: 1.2;
}

.care-status__copy span {
  color: #687c72;
  font-size: 12px;
  line-height: 1.15;
}

.care-status__arrow {
  flex: 0 0 auto;
  color: #729082 !important;
  font-size: 18px !important;
}

.care-status--stale {
  border-color: #eed9ae;
  background: #fff9ed;
}

.care-status--stale .care-status__icon {
  color: #a56800;
  background: #f9e9c8;
}

.care-status--stale .care-status__copy strong {
  color: #70501b;
}

.care-status--missing {
  border-color: #efc8ca;
  background: #fff4f4;
}

.care-status--missing .care-status__icon {
  color: #b53a3f;
  background: #f7dcde;
}

.care-status--missing .care-status__copy strong {
  color: #843036;
}

.care-status--unavailable {
  border-color: #d8e0dc;
  background: #f6f8f7;
}

.care-status--unavailable .care-status__icon {
  color: #687970;
  background: #e5ebe8;
}

.care-status--unavailable .care-status__copy strong {
  color: #526158;
}

.trainer-recommendation {
  display: grid;
  width: 100%;
  min-height: 50px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  text-align: left;
  color: #725012;
  border: 1px solid #efd4a1;
  border-radius: 12px;
  background: linear-gradient(135deg, #fffaf0, #fff4dc);
  cursor: pointer;
}

.trainer-recommendation__icon {
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  color: #a66b00;
  border-radius: 10px;
  background: rgba(239, 165, 33, 0.16);
}

.trainer-recommendation__icon >>> .v-icon {
  color: currentColor;
  font-size: 18px;
}

.trainer-recommendation__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.trainer-recommendation__copy small {
  color: currentColor;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.05;
  letter-spacing: 0.06em;
  opacity: 0.76;
}

.trainer-recommendation__copy strong {
  color: #593d0c;
  font-size: 14px;
  line-height: 1.2;
}

.trainer-recommendation__copy span {
  display: none;
}

.trainer-recommendation__action {
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  gap: 3px;
  padding: 0 8px;
  color: currentColor;
  font-size: 12px;
  font-weight: 900;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.72);
  white-space: nowrap;
}

.trainer-recommendation__action >>> .v-icon {
  color: currentColor;
  font-size: 17px;
}

.trainer-recommendation--red {
  color: #a43238;
  border-color: #efc4c7;
  background: linear-gradient(135deg, #fff7f7, #ffeded);
}

.trainer-recommendation--red .trainer-recommendation__icon {
  color: #b53a3f;
  background: #f8dfe1;
}

.trainer-recommendation--red .trainer-recommendation__copy strong {
  color: #792b31;
}

.trainer-recommendation--red .trainer-recommendation__copy span {
  color: #855b5e;
}

.trainer-recommendation--blue {
  color: #176fae;
  border-color: #c6e1f3;
  background: linear-gradient(135deg, #f4fbff, #eaf6fd);
}

.trainer-recommendation--blue .trainer-recommendation__icon {
  color: #176fae;
  background: #dceffb;
}

.trainer-recommendation--blue .trainer-recommendation__copy strong {
  color: #145985;
}

.trainer-recommendation--blue .trainer-recommendation__copy span {
  color: #5b7484;
}

.trainer-recommendation--violet {
  color: #7043a0;
  border-color: #dfcaec;
  background: linear-gradient(135deg, #fcf8ff, #f5ecfb);
}

.trainer-recommendation--violet .trainer-recommendation__icon {
  color: #7043a0;
  background: #eadcf3;
}

.trainer-recommendation--violet .trainer-recommendation__copy strong {
  color: #59347d;
}

.trainer-recommendation--violet .trainer-recommendation__copy span {
  color: #74627f;
}

.trainer-recommendation--green {
  color: #087b46;
  border-color: #c4e4d3;
  background: linear-gradient(135deg, #f4fbf7, #eaf7f0);
}

.trainer-recommendation--green .trainer-recommendation__icon {
  color: #087b46;
  background: #d9eee3;
}

.trainer-recommendation--green .trainer-recommendation__copy strong {
  color: #17613e;
}

.trainer-recommendation--green .trainer-recommendation__copy span {
  color: #5d7468;
}

.appointment-preview,
.no-appointment-hint {
  min-height: 44px;
  border-radius: 12px;
}

.appointment-preview {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  text-align: left;
  border: 1px solid #cfe6d9;
  background: #f1faf5;
  cursor: pointer;
}

.appointment-preview__icon {
  display: grid;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  place-items: center;
  border-radius: 10px;
  background: #d9f1e4;
}

.appointment-preview__icon >>> .v-icon {
  color: #087c47;
  font-size: 18px;
}

.appointment-preview__content {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.appointment-preview__content small,
.appointment-preview__content span {
  color: var(--tp-muted);
  font-size: 12px;
}

.appointment-preview__content strong {
  overflow: hidden;
  color: #1f3d2e;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.appointment-preview__arrow {
  color: #5e7d6d !important;
}

.no-appointment-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  color: #7a6a4c;
  font-size: 12px;
  font-weight: 700;
  border: 1px dashed #e8d2a8;
  background: #fffaf0;
}

.no-appointment-hint >>> .v-icon {
  color: #c48718;
  font-size: 19px;
}

.customer-actions {
  gap: 6px;
  margin-top: auto;
}

.action-button {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 11px;
  font-size: 13px;
  font-weight: 900;
  border-radius: 12px;
  cursor: pointer;
}

.action-button:disabled {
  cursor: default;
  filter: grayscale(0.4);
  opacity: 0.42;
}

.action-button >>> .v-icon {
  color: currentColor;
  font-size: 20px;
}

.action-button--primary {
  flex: 1.15;
  color: #fff;
  border: 1px solid var(--tp-green);
  background: linear-gradient(135deg, #0baa61, #078749);
  box-shadow: 0 8px 16px rgba(7, 150, 83, 0.17);
}

.action-button--secondary {
  flex: 1;
  color: #1e6c49;
  border: 1px solid #bcdcca;
  background: #eef8f3;
}

.action-button--file {
  width: 44px;
  flex: 0 0 44px;
  padding: 0;
  color: #526a5e;
  border: 1px solid var(--tp-line);
  background: #fff;
}

.no-account-note {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #9b5e0b;
  font-size: 12px;
}

.no-account-note >>> .v-icon {
  color: #c78220;
  font-size: 17px;
}

.state-message,
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: 1px solid var(--tp-line);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.82);
}

.state-message {
  min-height: 90px;
  gap: 12px;
  padding: 18px;
}

.state-message > div,
.empty-state {
  flex-direction: column;
}

.state-message strong,
.state-message span,
.empty-state strong,
.empty-state span {
  display: block;
  font-size: 13px;
}

.state-message span,
.empty-state span {
  color: var(--tp-muted);
}

.state-message--error >>> .v-icon {
  color: #d43c45;
}

.state-message button,
.empty-state button,
.load-more button {
  min-height: 44px;
  padding: 0 16px;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  border: 0;
  border-radius: 12px;
  background: var(--tp-green);
  cursor: pointer;
}

.empty-state {
  min-height: 260px;
  gap: 7px;
  padding: 28px;
}

.empty-state >>> .v-icon {
  margin-bottom: 4px;
  color: #82918a;
  font-size: 45px;
}

.empty-state button {
  margin-top: 9px;
}

.customer-skeleton {
  min-height: 285px;
  border: 1px solid #e0eae5;
  border-radius: 21px;
  background: linear-gradient(100deg, #edf3f0 25%, #f8fbf9 40%, #edf3f0 60%);
  background-size: 240% 100%;
  animation: skeleton 1.4s ease infinite;
}

@keyframes skeleton {
  to {
    background-position-x: -120%;
  }
}

.load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
}

.load-more span,
.limit-note {
  color: var(--tp-muted);
  font-size: 12px;
}

.limit-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 14px;
}

.limit-note >>> .v-icon {
  color: #7b8d83;
  font-size: 18px;
}

.template-overlay {
  position: fixed;
  z-index: 2300;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px;
  background: rgba(20, 35, 28, 0.62);
  backdrop-filter: blur(5px);
}

.template-sheet {
  display: flex;
  width: min(900px, 100%);
  max-height: min(820px, calc(100vh - 44px));
  flex-direction: column;
  padding: 22px;
  overflow: hidden;
  border: 1px solid #dce8e2;
  border-radius: 25px;
  background: #f8fbf9;
  box-shadow: 0 30px 70px rgba(20, 45, 32, 0.25);
}

.template-sheet__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}

.template-sheet__header h2 {
  margin: 3px 0 4px;
  font-size: 24px;
}

.template-search {
  width: 100%;
  margin: 18px 0 14px;
}

.template-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  padding: 1px 3px 5px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.template-option {
  display: flex;
  min-height: 78px;
  align-items: center;
  gap: 11px;
  padding: 11px;
  text-align: left;
  border: 1px solid #dce8e2;
  border-radius: 17px;
  background: #fff;
  cursor: pointer;
}

.template-option__icon {
  display: grid;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  place-items: center;
  border-radius: 13px;
}

.template-option__icon >>> .v-icon {
  font-size: 22px;
}

.template-option__text {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.template-option__text strong {
  overflow: hidden;
  color: var(--tp-ink);
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-option__text small {
  color: var(--tp-muted);
  font-size: 12px;
}

.template-option__arrow {
  color: #789087 !important;
  font-size: 19px !important;
}

.template-empty {
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--tp-muted);
}

.template-empty >>> .v-icon {
  margin-bottom: 8px;
  color: #80958a;
  font-size: 42px;
}

.template-empty strong,
.template-empty span {
  font-size: 13px;
}

.template-fade-enter-active,
.template-fade-leave-active {
  transition: opacity 0.16s ease;
}

.template-fade-enter,
.template-fade-leave-to {
  opacity: 0;
}

@media (hover: hover) and (pointer: fine) {
  .template-option:hover,
  .appointment-preview:hover,
  .care-status:hover,
  .trainer-recommendation:hover,
  .action-button:hover:not(:disabled),
  .view-tabs > button:hover {
    transform: translateY(-1px);
    filter: brightness(0.985);
  }
}

@media (max-width: 1500px) {
  .customer-grid,
  .skeleton-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1100px) {
  .trainerpoint-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .customer-search {
    width: 100%;
  }

  .template-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .trainerpoint-live {
    padding: 16px 14px 28px;
  }

  .customer-grid,
  .skeleton-grid {
    grid-template-columns: 1fr;
  }

  .view-tabs {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .view-tabs > button {
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .trainerpoint-header {
    align-items: flex-start;
  }

  .trainerpoint-title p,
  .trainerpoint-updated {
    display: none;
  }

  .trainerpoint-header__actions {
    gap: 6px;
  }

  .customer-actions {
    display: grid;
    grid-template-columns: 1fr 1fr 44px;
  }

  .care-overview {
    grid-template-columns: 1fr;
  }

  .trainer-recommendation {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .trainer-recommendation__action {
    min-height: 32px;
    grid-column: 2;
    justify-self: start;
  }

  .action-button {
    padding: 0 9px;
  }

  .template-overlay {
    align-items: flex-end;
    padding: 0;
  }

  .template-sheet {
    width: 100%;
    max-height: 92vh;
    padding: 18px 14px;
    border-radius: 24px 24px 0 0;
  }

  .template-sheet__header h2 {
    font-size: 20px;
  }

  .template-sheet__header p {
    display: none;
  }

  .template-grid {
    grid-template-columns: 1fr;
  }
}

@media (pointer: coarse) {
  .trainerpoint-live *,
  .trainerpoint-live *::before,
  .trainerpoint-live *::after {
    transition: none !important;
  }

  .spinning,
  .customer-skeleton {
    animation-duration: 1.8s;
  }
}

@media (prefers-reduced-motion: reduce) {
  .spinning,
  .customer-skeleton {
    animation: none;
  }
}
</style>
